<template>
  <div class="container-fluid py-4">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12 d-flex justify-content-between align-items-center">
        <h5 class="text-dark mb-0 fw-bold">{{ $t('authorizationReview.title') }}</h5>

        <div class="d-flex gap-2">
          <!-- ✅ NEW BUTTON (admin only) -->
         <button
  v-if="isAdmin"
  class="btn btn-outline-dark"
  @click="goMyAuthorization"
>
  <i class="fas fa-user-clock me-1"></i>
  {{ $t('authorizationReview.myAuthorization') }}  
</button>



          <button class="btn btn-outline-secondary" @click="exportToExcel">
            <i class="fas fa-file-export me-1"></i> {{ $t('authorizationReview.export') }}
          </button>
          <button class="btn btn-outline-primary" @click="refreshData">
            <i class="fas fa-sync me-1"></i> {{ $t('authorizationReview.refresh') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Stats -->
    <div class="row mb-4">
      <div class="col-md-3 mb-3" v-for="card in cards" :key="card.label">
        <div class="card border-0 shadow-sm">
          <div class="card-body text-center">
            <div class="fs-2 mb-2" :class="card.iconClass"><i :class="card.icon"></i></div>
            <h6 class="fw-bold mb-1">{{ card.value }}</h6>
            <p class="text-muted mb-0 small">{{ $t(`authorizationReview.stats.${card.label}`) }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="card shadow-sm border-0 mb-4">
      <div class="card-header bg-burgundy text-white py-3">
        <h6 class="mb-0 fw-bold text-white">{{ $t('authorizationReview.requestsTitle') }}</h6>
      </div>

      <div class="card-body border-bottom">
        <div class="row">
          <div class="col-md-4 mb-2 mb-md-0">
            <div class="input-group">
              <span class="input-group-text bg-white border-end-0">
                <i class="fas fa-search text-muted"></i>
              </span>
              <input
                type="text"
                v-model="searchQuery"
                class="form-control border-start-0"
                :placeholder="$t('authorizationReview.searchPlaceholder')"
              />
            </div>
          </div>
          <div class="col-md-4 mb-2 mb-md-0">
            <select v-model="statusFilter" class="form-select">
              <option value="all">{{ $t('authorizationReview.allStatus') }}</option>
              <option value="Pending">{{ $t('authorizationReview.status.pending') }}</option>
              <option value="Approved">{{ $t('authorizationReview.status.approved') }}</option>
              <option value="Rejected">{{ $t('authorizationReview.status.rejected') }}</option>
            </select>
          </div>
          <div class="col-md-4">
            <input type="date" v-model="dateFilter" class="form-control" />
          </div>
        </div>
      </div>

      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover m-0">
            <thead class="bg-light">
              <tr>
                <th class="ps-4">{{ $t('authorizationReview.table.employee') }}</th>
                <th>{{ $t('authorizationReview.table.date') }}</th>
                <th>{{ $t('authorizationReview.table.time') }}</th>
                <th>{{ $t('authorizationReview.table.duration') }}</th>
                <th>{{ $t('authorizationReview.table.status') }}</th>
                <th>{{ $t('authorizationReview.table.reason') }}</th>
                <th class="text-center">{{ $t('authorizationReview.table.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="7" class="text-center py-4">
                  <i class="fas fa-spinner fa-spin me-2"></i> {{ $t('authorizationReview.loading') }}
                </td>
              </tr>

              <tr v-else-if="filteredRequests.length === 0">
                <td colspan="7" class="text-center py-5 text-muted">
                  <i class="fas fa-schedule mb-2" style="font-size:1.6rem;"></i>
                  <div>{{ $t('authorizationReview.noRequests') }}</div>
                </td>
              </tr>

              <template v-else>
                <tr v-for="r in filteredRequests" :key="r.id">
                  <td class="ps-4">
                    <div class="fw-semibold">
                      <span v-if="nameCache.get(r.user_id)">{{ nameCache.get(r.user_id) }}</span>
                      <span v-else class="text-muted">
                        <i class="fas fa-spinner fa-spin me-1"></i>#{{ r.user_id }}
                      </span>
                    </div>
                    <div class="text-muted small" v-if="emailCache.get(r.user_id)">
                      {{ emailCache.get(r.user_id) }}
                    </div>
                  </td>
                  <td>{{ formatDate(r.date) }}</td>
                  <td>{{ r.start_time }} - {{ r.end_time }}</td>
                  <td>{{ r.duration_hours }}h</td>
                  <td>
                    <span class="badge rounded-pill" :class="getStatusClass(r.status)">
                      {{ $t(`authorizationReview.status.${r.status.toLowerCase()}`) }}
                    </span>
                  </td>
                  <td>
                    <div class="text-truncate" style="max-width: 220px" :title="r.reason">
                      {{ r.reason || $t('authorizationReview.noReason') }}
                    </div>
                  </td>
                  <td>
                    <div class="d-flex justify-content-center gap-2">
                      <button
                        v-if="r.status==='Pending'"
                        class="btn btn-ico btn-outline-success"
                        @click="approveRequest(r.id)"
                        :title="$t('authorizationReview.actions.approve')"
                      >
                        <i class="fas fa-check"></i>
                      </button>
                      <button
                        v-if="r.status==='Pending'"
                        class="btn btn-ico btn-outline-danger"
                        @click="rejectRequest(r.id)"
                        :title="$t('authorizationReview.actions.reject')"
                      >
                        <i class="fas fa-times"></i>
                      </button>
                      <button
                        class="btn btn-ico btn-outline-info"
                        @click="viewDetails(r)"
                        :title="$t('authorizationReview.actions.view')"
                      >
                        <i class="fas fa-eye"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="px-4 py-3 d-flex justify-content-end" v-if="pagination.total>0">
        <button class="nav-circle me-2" :disabled="pagination.current_page===1" @click="loadPage(pagination.current_page-1)">
          <i class="fas fa-chevron-left"></i>
        </button>
        <button class="nav-circle" :disabled="pagination.current_page===pagination.last_page" @click="loadPage(pagination.current_page+1)">
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Modal -->
    <div
      class="modal fade show"
      v-if="showModal && selectedRequest"
      style="display: block; background: rgba(0,0,0,.5)"
      @click.self="showModal=false"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-burgundy text-white">
            <h5 class="modal-title">{{ $t('authorizationReview.detailsTitle') }}</h5>
            <button type="button" class="btn-close btn-close-white" @click="showModal=false"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-6"><strong>{{ $t('authorizationReview.table.employee') }}:</strong><br>
                <span>{{ nameCache.get(selectedRequest.user_id) || ('#' + selectedRequest.user_id) }}</span>
              </div>
              <div class="col-6" v-if="emailCache.get(selectedRequest.user_id)">
                <strong>Email:</strong><br><span>{{ emailCache.get(selectedRequest.user_id) }}</span>
              </div>
              <div class="col-6"><strong>{{ $t('authorizationReview.table.date') }}:</strong><br><span>{{ formatDate(selectedRequest.date) }}</span></div>
              <div class="col-6"><strong>{{ $t('authorizationReview.table.status') }}:</strong><br>
                <span class="badge" :class="getStatusClass(selectedRequest.status)">
                  {{ $t(`authorizationReview.status.${selectedRequest.status.toLowerCase()}`) }}
                </span>
              </div>
              <div class="col-6"><strong>{{ $t('authorizationReview.modal.startTime') }}:</strong><br><span>{{ selectedRequest.start_time }}</span></div>
              <div class="col-6"><strong>{{ $t('authorizationReview.modal.endTime') }}:</strong><br><span>{{ selectedRequest.end_time }}</span></div>
              <div class="col-6"><strong>{{ $t('authorizationReview.modal.duration') }}:</strong><br><span>{{ selectedRequest.duration_hours }}h</span></div>
              <div class="col-6"><strong>{{ $t('authorizationReview.modal.submitted') }}:</strong><br><span>{{ formatDateTime(selectedRequest.created_at) }}</span></div>
              <div class="col-12" v-if="selectedRequest.reason"><strong>{{ $t('authorizationReview.table.reason') }}:</strong><br><span>{{ selectedRequest.reason }}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useRouter } from 'vue-router'
import axios from '@/api'
import * as XLSX from 'xlsx'

const toast = useToast()
const router = useRouter()


const loading = ref(true)
const requests = ref([])
const searchQuery = ref('')
const statusFilter = ref('all')
const dateFilter = ref('')
const selectedRequest = ref(null)
const showModal = ref(false)
const pagination = ref({ current_page: 1, last_page: 1, total: 0 })


const rawUser = localStorage.getItem('user')
const currentUser = rawUser ? JSON.parse(rawUser) : {}
const isAdmin = computed(() => {
  if (currentUser?.role === 'admin') return true
  const roles = currentUser?.roles
  if (Array.isArray(roles)) {
    return roles.some(r => (typeof r === 'string' ? r === 'admin' || r === 'SuperAdmin' : r?.name === 'admin' || r?.name === 'SuperAdmin'))
  }
  if (typeof roles === 'string') return roles === 'admin' || roles === 'SuperAdmin'
  return false
})


function goMyAuthorization() {
  router.push('/authorization-request')
}

const nameCache = ref(new Map()).value
const emailCache = ref(new Map()).value


const EMPLOYEES_LIST_URL = '/getAllUser'   

const stats = computed(() => {
  const pending  = requests.value.filter(r => r.status === 'Pending').length
  const approved = requests.value.filter(r => r.status === 'Approved').length
  const rejected = requests.value.filter(r => r.status === 'Rejected').length
  return { pending, approved, rejected, total: requests.value.length }
})

const cards = computed(() => ([
  { label: 'Pending',  value: stats.value.pending,  icon: 'fas fa-clock',        iconClass: 'text-warning' },
  { label: 'Approved', value: stats.value.approved, icon: 'fas fa-check-circle', iconClass: 'text-success' },
  { label: 'Rejected', value: stats.value.rejected, icon: 'fas fa-times-circle', iconClass: 'text-danger' },
  { label: 'Total',    value: stats.value.total,    icon: 'fas fa-list',         iconClass: 'text-info' },
]))

const filteredRequests = computed(() => {
  const q = (searchQuery.value || '').toLowerCase()
  return requests.value.filter(r => {
    const displayName = (nameCache.get(r.user_id) || '').toLowerCase()
    const nameMatch   = displayName.includes(q)
    const statusMatch = statusFilter.value === 'all' || r.status === statusFilter.value
    const dateMatch   = !dateFilter.value || r.date === dateFilter.value
    return nameMatch && statusMatch && dateMatch
  })
})


function formatDate(d) { return d ? new Date(d).toLocaleDateString('en-GB') : 'N/A' }
function formatDateTime(d) { return d ? new Date(d).toLocaleString('en-GB') : 'N/A' }
function getStatusClass(s) {
  switch (s) {
    case 'Approved': return 'bg-success'
    case 'Rejected': return 'bg-danger'
    case 'Pending':  return 'bg-warning'
    default:         return 'bg-secondary'
  }
}


onMounted(async () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  if (!user.id) {
    toast.error('❌ Veuillez vous connecter.')
    router.push({ name: 'SignIn' })
    return
  }
  await fetchRequests()
})

async function fetchRequests(page = 1) {
  try {
    loading.value = true
    const { data } = await axios.get('/employee/authorizations', { params: { page } })
    requests.value = data.authorizations?.data || data.authorizations || []
    pagination.value = {
      current_page: data.authorizations?.current_page ?? 1,
      last_page:    data.authorizations?.last_page ?? 1,
      total:        data.authorizations?.total ?? data.total ?? requests.value.length
    }

    
    const ids = [...new Set(requests.value.map(r => r.user_id).filter(Boolean))]
    await primeUsersFromGetAllUser(ids)
  } catch (e) {
    console.error('Error fetching requests:', e)
    toast.error('Failed to load authorization requests')
  } finally {
    loading.value = false
  }
}

function pickUserFields(raw) {
  if (!raw) return null
  const id = raw.id ?? raw.user_id
  const firstname = raw.firstname ?? raw.first_name ?? raw.prenom ?? ''
  const lastname  = raw.lastname  ?? raw.last_name  ?? raw.nom    ?? ''
  const name = (firstname || lastname) ? `${firstname} ${lastname}`.trim() : (raw.name || '')
  const email = raw.email || ''
  if (!id) return null
  return { id, name: name || `#${id}`, email }
}

async function primeUsersFromGetAllUser(targetIds = []) {
  const wanted = new Set(targetIds.filter(id => !nameCache.has(id)))
  if (wanted.size === 0) return

  let page = 1
  const per_page = 200
  const maxPages = 50

  while (wanted.size > 0 && page <= maxPages) {
    let resp
    try {
      resp = await axios.get(EMPLOYEES_LIST_URL, { params: { page, per_page } })
    } catch {
      break
    }
    const list = Array.isArray(resp?.data?.data) ? resp.data.data : []
    for (const u of list) {
      const picked = pickUserFields(u)
      if (!picked) continue
      if (!nameCache.has(picked.id)) nameCache.set(picked.id, picked.name)
      if (picked.email && !emailCache.has(picked.id)) emailCache.set(picked.id, picked.email)
      wanted.delete(picked.id)
    }

    const cur = resp?.data?.current_page ?? page
    const last = resp?.data?.last_page ?? page
    if (cur >= last) break
    page = cur + 1
  }
}


function loadPage(page) {
  if (page < 1 || page > pagination.value.last_page) return
  fetchRequests(page)
}

async function approveRequest(id) {
  try {
    await axios.post(`/employee/authorizations/${id}/approve`)
    toast.success('Request approved')
    fetchRequests(pagination.value.current_page)
  } catch (e) {
    const msg = e?.response?.status === 403
      ? 'Accès refusé : seul le superviseur du demandeur peut approuver.'
      : (e?.response?.data?.message || 'Failed to approve request')
    toast.error(msg)
  }
}

async function rejectRequest(id) {
  try {
    await axios.post(`/employee/authorizations/${id}/reject`)
    toast.success('Request rejected')
    fetchRequests(pagination.value.current_page)
  } catch (e) {
    const msg = e?.response?.status === 403
      ? 'Accès refusé : seul le superviseur du demandeur peut rejeter.'
      : (e?.response?.data?.message || 'Failed to reject request')
    toast.error(msg)
  }
}

function viewDetails(r) { selectedRequest.value = r; showModal.value = true }
function refreshData()  { fetchRequests(pagination.value.current_page) }

function exportToExcel() {
  const rows = filteredRequests.value.map(r => ({
    Employee: nameCache.get(r.user_id) || `#${r.user_id}`,
    Email: emailCache.get(r.user_id) || '',
    Date: formatDate(r.date),
    'Start Time': r.start_time,
    'End Time': r.end_time,
    'Duration (hours)': r.duration_hours,
    Status: r.status,
    Reason: r.reason || 'No reason provided',
    'Submitted At': formatDateTime(r.created_at)
  }))
  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Authorization_Requests')
  XLSX.writeFile(wb, 'Authorization_Requests_Export.xlsx')
}
</script>

<style scoped>
.bg-burgundy { background: linear-gradient(195deg, #4b5563, #8d2e38); }
.table th { font-size:.875rem; text-transform:uppercase; letter-spacing:.025em; }
.table td { padding-top:.75rem; padding-bottom:.75rem; vertical-align:middle; }
.input-group-text, .form-control { padding:.6rem 1rem; border-color:#e9ecef; }
.form-select { padding:.6rem 2rem .6rem 1rem; border-color:#e9ecef; background:#fff; }
.btn { padding:.5rem 1rem; font-weight:600; border-radius:.25rem; text-transform:uppercase; font-size:.875rem; }
.btn-ico { width:40px; height:40px; display:inline-flex; align-items:center; justify-content:center; border-radius:.5rem; font-size:1.05rem; }
.nav-circle { width:42px; height:42px; border-radius:999px; border:1px solid #e9ecef; background:#fff; color:#b03a45; display:inline-flex; align-items:center; justify-content:center; box-shadow:0 2px 6px rgba(0,0,0,.06); transition:.15s; }
.nav-circle:hover { background:#b03a45; color:#fff; border-color:#b03a45; }
.nav-circle:disabled { opacity:.45; cursor:not-allowed; background:#fff; color:#b03a45; border-color:#e9ecef; box-shadow:none; }
.fw-semibold { font-weight:600!important; } .fw-bold { font-weight:700!important; }
.container-fluid { max-width:1320px; } .card { border-radius:.5rem; overflow:hidden; } .card-header { border-bottom:none; }
</style>
